import { memo } from "react";

import {
  type ProductOption,
  type Maybe,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Badge, type BadgeType } from "@components/Badge";
import { Heading } from "@components/Heading";
import { Image } from "@components/Image";
import { Link } from "@components/Link";
import { Price } from "@components/Price";
import { type CompareAtPriceDisplay } from "@components/Price/constants";
import { Text } from "@components/Text";
import { type CartMediaImage, type OPProductVariant } from "@tablescape/state";
import { tw } from "classnames";

import { ProductCardOptions } from "./ProductCardOptions.data";

type Props = {
  activeVariant: OPProductVariant;
  activeVariantUrl: string;
  badgeText: Maybe<string>;
  badgeType: Maybe<BadgeType>;
  colorOption?: Omit<ProductOption, "values">;
  compareAtPriceDisplay?: Maybe<CompareAtPriceDisplay>;
  description: Maybe<string>;
  labels?: string[];
  nextLabel: string;
  prevLabel: string;
  setSize?: Maybe<string>;
  title: string;
};

const styles = {
  base: tw("block"),
  image: tw("bg-background-secondary", "relative"),
  imageFadeIn: tw("animate-fade-in"),
  hoverImage: tw(
    "absolute",
    "left-0",
    "top-0",
    "hidden",
    "opacity-0",
    "transition-opacity",
    "md:block",
    "md:hover:opacity-100"
  ),
  badge: tw("absolute", "left-2", "top-2", "md:left-4", "md:top-4"),
  content: tw(
    "mt-2",
    "flex",
    "flex-1",
    "flex-col",
    "justify-between",
    "md:mt-4"
  ),
  titleContainer: tw("flex", "gap-x-1", "mb-1", "items-baseline", "flex-wrap"),
  title: tw("text-primary"),
  setSize: tw("text-primary"),
  description: tw("mb-1.5", "hidden", "text-primary-muted", "md:block"),
  price: tw("mb-2"),
  labels: (colorOptionValueLength: number) =>
    tw("flex", "overflow-x-auto", "flex", "flex-wrap", "gap-2", "p-0", {
      [tw("mt-1", "md:mt-2")]: colorOptionValueLength === 0,
      [tw("mt-4")]: colorOptionValueLength > 0,
    }),
  label: tw("hidden", "first:flex", "md:flex"),
};

const ProductCardViewBase = ({
  activeVariant,
  activeVariantUrl,
  badgeText,
  badgeType,
  colorOption,
  compareAtPriceDisplay,
  description,
  labels,
  nextLabel,
  prevLabel,
  setSize,
  title,
}: Props) => (
  <li className={styles.base}>
    <div className={styles.image}>
      <Link
        data-mtr-as="clickProduct"
        data-mtr-canonical-href={activeVariantUrl}
        data-mtr-product-variant-name={activeVariant.title}
        data-mtr-track="click"
        to={activeVariantUrl}
      >
        {activeVariant.media[0] && (
          <div className={styles.imageFadeIn}>
            <Image
              aspectRatio="1/1"
              data={(activeVariant.media[0] as CartMediaImage).image}
              height={476}
              width={476}
            />
          </div>
        )}
        {activeVariant.hoverImage && (
          <div className={styles.hoverImage}>
            <Image
              aspectRatio="1/1"
              data={activeVariant.hoverImage.image}
              height={476}
              width={476}
            />
          </div>
        )}
        {badgeText && (
          <div className={styles.badge}>
            <Badge
              size={{ sm: "sm", md: "md" }}
              {...(badgeType && { type: badgeType })}
            >
              {badgeText}
            </Badge>
          </div>
        )}
      </Link>
    </div>
    <div className={styles.content}>
      <div className={styles.titleContainer}>
        <Heading as="h6" className={styles.title}>
          {title}
        </Heading>
        {setSize && <Text className={styles.setSize}>({setSize})</Text>}
      </div>
      {description && <Text className={styles.description}>{description}</Text>}
      <div className={styles.price}>
        <Price
          compareAtPrice={activeVariant.compareAtPrice}
          compareAtPriceDisplay={compareAtPriceDisplay}
          price={activeVariant.price}
          size="md"
        />
      </div>
      <ProductCardOptions nextLabel={nextLabel} prevLabel={prevLabel} />
    </div>
    {labels?.length && (
      <ol className={styles.labels(colorOption?.optionValues.length ?? 0)}>
        {labels.map((label) => (
          <li className={styles.label} key={label}>
            <Badge type="quinary">{label}</Badge>
          </li>
        ))}
      </ol>
    )}
  </li>
);

export const ProductCardView = memo(ProductCardViewBase);
